import { Component } from '@theme/component';
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * VEYR bestsellers quick add.
 *
 * Two section-scoped custom elements:
 * - <veyr-bestsellers-component>: card CTAs, submit/added/error states, toast,
 *   local analytics events (stable keys only — never claim text).
 * - <veyr-quick-add-component>: the shared bottom sheet (extends the theme's
 *   DialogComponent for show/close/scroll-lock/Escape/outside-click), variant
 *   chip sync (hidden variant id, price, thumbnail, availability, CTA label).
 *
 * Cart mutations are exclusively handled by the theme's product-form-component
 * (Theme.routes.cart_add_url, CartAddEvent/CartErrorEvent). This module never
 * calls /cart/add itself.
 */

const STATE_RESET_DELAY = 1600;
const SHEET_CLOSE_DELAY = 650;
const TOAST_DURATION = 5000;
// product-form.js swallows network/non-JSON failures without dispatching any
// event; this watchdog un-bricks the CTA so the shopper can retry.
const ADD_WATCHDOG_TIMEOUT = 10000;

/**
 * Emits a section-local analytics event. Detail carries stable keys only.
 * @param {string} name
 * @param {Record<string, string | undefined>} detail
 */
function emitVeyrEvent(name, detail) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

/**
 * @param {Element | null} formComponent
 * @returns {HTMLButtonElement | null}
 */
function submitButtonOf(formComponent) {
  return formComponent?.querySelector('button[ref="addToCartButton"]') ?? null;
}

/**
 * @param {Element | null} formComponent
 * @param {string} state
 */
function setFormState(formComponent, state) {
  if (!(formComponent instanceof HTMLElement)) return;
  if (state) {
    formComponent.setAttribute('data-veyr-state', state);
  } else {
    formComponent.removeAttribute('data-veyr-state');
  }
}

class VeyrBestsellersComponent extends Component {
  /** @type {AbortController} */
  #abort;
  /** @type {Map<HTMLElement, number>} */
  #resetTimers = new Map();
  /** @type {number} */
  #toastTimer = 0;
  /** @type {number} */
  #toastHideTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });

    const toast = this.#toast;
    toast?.addEventListener('pointerenter', this.#pauseToast, { signal });
    toast?.addEventListener('focusin', this.#pauseToast, { signal });
    toast?.addEventListener('pointerleave', this.#restartToast, { signal });
    toast?.addEventListener('focusout', this.#restartToast, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    for (const timer of this.#resetTimers.values()) clearTimeout(timer);
    this.#resetTimers.clear();
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    if (this.#toastHideTimer) clearTimeout(this.#toastHideTimer);
  }

  get #toast() {
    return this.querySelector('[data-veyr-qa-toast]');
  }

  get #sheet() {
    const sheet = this.querySelector('veyr-quick-add-component');
    return sheet instanceof VeyrQuickAddComponent ? sheet : null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest('[data-veyr-qa-open]');
    if (opener instanceof HTMLElement) {
      const productId = opener.getAttribute('data-veyr-qa-open') || '';
      this.#sheet?.openFor(productId, opener);
      emitVeyrEvent('quick_add_open', {
        sectionId: this.dataset.sectionId,
        productId,
        claimKey: this.#claimKeyFor(productId),
      });
      return;
    }

    const productLink = event.target.closest('[data-veyr-product-link]');
    if (productLink) {
      const card = productLink.closest('[data-veyr-card]');
      emitVeyrEvent('product_click', {
        sectionId: this.dataset.sectionId,
        productId: card instanceof HTMLElement ? card.dataset.productId : undefined,
      });
    }
  };

  /** @param {string} productId */
  #claimKeyFor(productId) {
    const card = this.querySelector(`[data-veyr-card][data-product-id="${CSS.escape(productId)}"]`);
    const claim = card?.querySelector('[data-claim-key]');
    return claim instanceof HTMLElement ? claim.dataset.claimKey : undefined;
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (!(event.target instanceof Element)) return;
    const formComponent = event.target.closest('product-form-component');
    if (!(formComponent instanceof HTMLElement)) return;

    this.#clearResetTimer(formComponent);
    setFormState(formComponent, 'adding');
    const button = submitButtonOf(formComponent);
    if (button) button.disabled = true;

    // Watchdog: if neither cart:update nor cart:error ever arrives (swallowed
    // fetch failure in product-form.js), release the button so retry works.
    const watchdog = window.setTimeout(() => {
      this.#resetTimers.delete(formComponent);
      setFormState(formComponent, 'error');
      this.#reenable(formComponent);
      this.#scheduleReset(formComponent);
    }, ADD_WATCHDOG_TIMEOUT);
    this.#resetTimers.set(formComponent, watchdog);

    emitVeyrEvent('quick_add_submit', {
      sectionId: this.dataset.sectionId,
      productId: formComponent.dataset.productId,
      variantId: formComponent.querySelector('input[name="id"]')?.value,
    });
  };

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;
    const formComponent =
      event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!(formComponent instanceof HTMLElement)) return;

    if (event.detail?.data?.didError) {
      // cart:error already handled this failure; avoid a duplicate error emit.
      if (formComponent.getAttribute('data-veyr-state') !== 'error') {
        this.#resolveError(formComponent);
      }
      return;
    }

    setFormState(formComponent, 'added');
    this.#showToast();
    emitVeyrEvent('quick_add_success', {
      sectionId: this.dataset.sectionId,
      productId: formComponent.dataset.productId,
      variantId: event.detail?.sourceId,
    });

    this.#scheduleReset(formComponent);
  };

  /** @param {CustomEvent} event */
  #onCartError = (event) => {
    const formComponent =
      event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!(formComponent instanceof HTMLElement)) return;
    this.#resolveError(formComponent);
  };

  /** @param {HTMLElement} formComponent */
  #resolveError(formComponent) {
    this.#clearResetTimer(formComponent);
    setFormState(formComponent, 'error');
    this.#reenable(formComponent);
    emitVeyrEvent('quick_add_error', {
      sectionId: this.dataset.sectionId,
      productId: formComponent.dataset.productId,
    });
    this.#scheduleReset(formComponent);
  }

  /** @param {HTMLElement} formComponent */
  #scheduleReset(formComponent) {
    this.#clearResetTimer(formComponent);
    const timer = window.setTimeout(() => {
      setFormState(formComponent, '');
      this.#reenable(formComponent);
      // Restore keyboard focus lost when the focused CTA was disabled, but
      // only when focus actually fell back to <body>.
      const button = submitButtonOf(formComponent);
      if (button && !button.disabled && document.activeElement === document.body) {
        button.focus();
      }
      this.#resetTimers.delete(formComponent);
    }, STATE_RESET_DELAY);
    this.#resetTimers.set(formComponent, timer);
  }

  /** @param {HTMLElement} formComponent */
  #clearResetTimer(formComponent) {
    const timer = this.#resetTimers.get(formComponent);
    if (timer) clearTimeout(timer);
    this.#resetTimers.delete(formComponent);
  }

  /**
   * Re-enables the submit button unless the panel still requires an explicit
   * (fit-critical) choice or the selected variant is unavailable.
   * @param {HTMLElement} formComponent
   */
  #reenable(formComponent) {
    const button = submitButtonOf(formComponent);
    if (!button) return;

    const panel = formComponent.closest('[data-veyr-qa-panel]');
    if (panel) {
      const checked = panel.querySelector('input[data-veyr-qa-pick]:checked');
      if (!(checked instanceof HTMLInputElement)) return;
      if (checked.dataset.available !== 'true') return;
    }

    button.disabled = false;
  }

  #showToast = () => {
    const toast = this.#toast;
    if (!(toast instanceof HTMLElement)) return;
    if (this.#toastHideTimer) {
      clearTimeout(this.#toastHideTimer);
      this.#toastHideTimer = 0;
    }
    toast.hidden = false;
    // Force a frame so the transition runs when re-shown quickly.
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    this.#restartToast();
  };

  #pauseToast = () => {
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    this.#toastTimer = 0;
  };

  #restartToast = () => {
    this.#pauseToast();
    this.#toastTimer = window.setTimeout(() => {
      const toast = this.#toast;
      if (!(toast instanceof HTMLElement)) return;
      toast.classList.remove('is-visible');
      this.#toastHideTimer = window.setTimeout(() => {
        toast.hidden = true;
        this.#toastHideTimer = 0;
      }, 300);
    }, TOAST_DURATION);
  };
}

class VeyrQuickAddComponent extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {AbortController} */
  #abort;
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #activePanel = null;
  /** @type {number} */
  #closeTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onPick, { signal });
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
  }

  disconnectedCallback() {
    // A section re-render (theme editor) can remove an open dialog; undo the
    // body scroll lock DialogComponent applied in showDialog.
    const dialog = this.refs?.dialog;
    if (dialog?.open) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      dialog.close();
    }
    super.disconnectedCallback();
    this.#abort.abort();
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
  }

  /**
   * Opens the sheet with the panel for the given product.
   * @param {string} productId
   * @param {HTMLElement} [trigger] - The card button that opened the sheet.
   */
  openFor(productId, trigger) {
    const panel = this.querySelector(
      `[data-veyr-qa-panel][data-product-id="${CSS.escape(productId)}"]`
    );
    if (!(panel instanceof HTMLElement)) return;

    this.#trigger = trigger ?? null;
    for (const other of this.querySelectorAll('[data-veyr-qa-panel]')) {
      other.hidden = other !== panel;
    }
    this.#activePanel = panel;

    const title = panel.querySelector('.veyr-qa-panel__title');
    if (title instanceof HTMLElement && title.id) {
      this.refs.dialog.setAttribute('aria-labelledby', title.id);
      this.refs.dialog.removeAttribute('aria-label');
    } else {
      this.refs.dialog.setAttribute('aria-label', 'Choose options');
    }

    this.#syncFromChecked(panel);
    this.showDialog();
  }

  #onDialogOpen = () => {
    // showDialog defers showModal to a frame; focus after it settled.
    requestAnimationFrame(() => {
      const panel = this.#activePanel;
      if (!panel) return;
      const target =
        panel.querySelector('input[data-veyr-qa-pick]:checked:not(:disabled)') ||
        panel.querySelector('input[data-veyr-qa-pick]:not(:disabled)') ||
        this.refs.closeButton;
      if (target instanceof HTMLElement) target.focus();
    });
  };

  #onDialogClose = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = 0;
    }
    emitVeyrEvent('quick_add_close', {
      sectionId: this.dataset.sectionId,
      productId: this.#activePanel?.dataset.productId,
    });
    const trigger = this.#trigger;
    this.#trigger = null;
    if (trigger?.isConnected) trigger.focus();
  };

  /** @param {Event} event */
  #onPick = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.hasAttribute('data-veyr-qa-pick') || !input.checked) return;

    const panel = input.closest('[data-veyr-qa-panel]');
    if (!(panel instanceof HTMLElement)) return;

    this.#applySelection(panel, input);
    emitVeyrEvent('variant_selected', {
      sectionId: this.dataset.sectionId,
      productId: panel.dataset.productId,
      variantId: input.value,
    });
  };

  /** @param {HTMLElement} panel */
  #syncFromChecked(panel) {
    const checked = panel.querySelector('input[data-veyr-qa-pick]:checked');
    if (checked instanceof HTMLInputElement) {
      this.#applySelection(panel, checked);
    }
  }

  /**
   * Variant selection updates hidden variant id, price, thumbnail,
   * availability note, CTA label/state and the PDP link together.
   * @param {HTMLElement} panel
   * @param {HTMLInputElement} input
   */
  #applySelection(panel, input) {
    const formComponent = panel.querySelector('product-form-component');
    const available = input.dataset.available === 'true';

    const hidden = formComponent?.querySelector('input[name="id"]');
    if (hidden instanceof HTMLInputElement) hidden.value = input.value;

    const price = panel.querySelector('[data-veyr-qa-price]');
    if (price instanceof HTMLElement && input.dataset.price) {
      price.textContent = input.dataset.price;
    }

    const thumb = panel.querySelector('[data-veyr-qa-thumb]');
    if (thumb instanceof HTMLImageElement && input.dataset.image) {
      thumb.src = input.dataset.image;
    }

    const note = panel.querySelector('[data-veyr-qa-note]');
    if (note instanceof HTMLElement) note.hidden = available;

    const button = submitButtonOf(formComponent);
    if (button) {
      button.disabled = !available;
      const label = button.querySelector('.add-to-cart-text__content span span');
      if (label) {
        label.textContent = available
          ? `Add to cart — ${input.dataset.price ?? ''}`.trim()
          : 'Sold out';
      }
    }

    const details = panel.querySelector('[data-veyr-qa-details]');
    if (details instanceof HTMLAnchorElement) {
      try {
        const url = new URL(details.href, window.location.origin);
        url.searchParams.set('variant', input.value);
        details.href = url.toString();
      } catch {
        // Leave the plain product URL when parsing fails.
      }
    }
  }

  /**
   * The sheet only closes after a successful cart update of its own active
   * form (the event bubbles up from the panel's product-form-component).
   * @param {CustomEvent} event
   */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (event.detail?.data?.didError) return;
    if (!this.refs.dialog.open) return;

    const formComponent =
      event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!formComponent || !this.#activePanel?.contains(formComponent)) return;

    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      this.#closeTimer = 0;
      this.closeDialog();
    }, SHEET_CLOSE_DELAY);
  };
}

if (!customElements.get('veyr-bestsellers-component')) {
  customElements.define('veyr-bestsellers-component', VeyrBestsellersComponent);
}

if (!customElements.get('veyr-quick-add-component')) {
  customElements.define('veyr-quick-add-component', VeyrQuickAddComponent);
}
